import Icon from '../../../components/appIcon';
import Button from '../../../components/ui/button';
import Input from '../../../components/ui/input';
import Select from '../../../components/ui/select';

const EmployeeFilters = ({
  searchTerm,
  onSearchChange,
  departmentFilter,
  onDepartmentChange,
  employmentTypeFilter,
  onEmploymentTypeChange,
  startDate,
  onStartDateChange,
  endDate,
  onEndDateChange,
  ontoggle,
  onClearFilters,
  onExport,
  departments,
  isCollapsed
}) => {
  const departmentOptions = [
    { value: "", label: "All Departments" },
    ...departments.map((d) => ({ value: d.name, label: d.name })),
  ];

  const employmentTypeOptions = [
    { value: "", label: "All Types" },
    { value: "permanent", label: "Permanent" },
    { value: "temporary", label: "Temporary" },
  ];

  const hasActiveFilters =
    searchTerm || departmentFilter || employmentTypeFilter || startDate || endDate;

  return (
    <div className="space-y-3">
      {/* Filter Toggle Button */}
      {!isCollapsed && (
        <button
          onClick={ontoggle}
          className="group relative flex items-center gap-2 px-4 py-2.5 text-sm bg-white rounded-lg hover:shadow-lg transition-all duration-300 border border-gray-100 overflow-hidden transform hover:-translate-y-0.5"
        >
          {/* Animated gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          
          {/* Animated circle decoration */}
          <div className="absolute -top-4 -right-4 w-12 h-12 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full opacity-20 group-hover:scale-150 transition-transform duration-700"></div>
          
          <div className="relative z-10 flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 text-blue-600 group-hover:scale-110 group-hover:rotate-12 transition-all duration-300">
              <Icon name="Filter" size={16} />
            </div>
            <span className="font-medium text-gray-700 group-hover:text-blue-600 transition-colors duration-300">
              Show Filters
            </span>
          </div>
        </button>
      )}

      {/* Animated Filter Panel */}
      <div
        className={`
          overflow-visible  transition-all duration-500 ease-out
          ${isCollapsed ? "max-h-[500px] opacity-100 translate-y-0" : "max-h-0 opacity-0 -translate-y-4"}
        `}
      >
        {isCollapsed && (
          <div className="relative bg-white rounded-xl p-4 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-visible group">

            {/* Animated gradient background */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-purple-50/30 to-pink-50/30 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
            
            {/* Decorative circles */}
            <div className="absolute -top-12 -right-12 w-32 h-32 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full opacity-10 group-hover:scale-150 transition-transform duration-1000"></div>
            <div className="absolute -bottom-8 -left-8 w-24 h-24 bg-gradient-to-br from-pink-100 to-purple-100 rounded-full opacity-10 group-hover:scale-150 transition-transform duration-1000"></div>

            <div className="relative z-10 space-y-3">
              {/* Top Row Inputs */}
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex-1 min-w-[200px] max-w-[300px] transform transition-all duration-300 hover:scale-[1.02]">
                  <Input
                    type="search"
                    placeholder="Search employees..."
                    value={searchTerm}
                    onChange={(e) => onSearchChange(e.target.value)}
                    className="h-9 text-sm border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all duration-300"
                  />
                </div>

                <div className="flex items-center gap-1 transform transition-all duration-300 hover:scale-[1.02]">
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => onStartDateChange(e.target.value)}
                    className="h-9 text-xs w-[130px] border-gray-200 focus:border-purple-400 focus:ring-2 focus:ring-purple-100 transition-all duration-300"
                  />
                  <span className="text-xs text-gray-400 font-medium px-1">to</span>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => onEndDateChange(e.target.value)}
                    className="h-9 text-xs w-[130px] border-gray-200 focus:border-purple-400 focus:ring-2 focus:ring-purple-100 transition-all duration-300"
                  />
                </div>

                <div className="transform transition-all duration-300 hover:scale-[1.02]">
                  <Select
                    options={departmentOptions}
                    value={departmentFilter}
                    onChange={onDepartmentChange}
                    className="h-9 text-sm w-[160px] border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all duration-300"
                  />
                </div>

                <div className="transform transition-all duration-300 hover:scale-[1.02]">
                  <Select
                    options={employmentTypeOptions}
                    value={employmentTypeFilter}
                    onChange={onEmploymentTypeChange}
                    className="h-9 text-sm w-[140px] border-gray-200 focus:border-pink-400 focus:ring-2 focus:ring-pink-100 transition-all duration-300"
                  />
                </div>

                {/* Right Buttons */}
                <div className="flex items-center gap-2 ml-auto">
                  {hasActiveFilters && (
                    <button
                      onClick={onClearFilters}
                      className="group/btn h-9 px-3 text-xs text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg flex items-center gap-1.5 transition-all duration-300 transform hover:scale-105"
                    >
                      <Icon name="X" size={14} className="group-hover/btn:rotate-90 transition-transform duration-300" />
                      Clear
                    </button>
                  )}

                  <button
                    onClick={onExport}
                    className="group/btn h-9 px-3 text-xs text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg flex items-center gap-1.5 transition-all duration-300 transform hover:scale-105"
                  >
                    <Icon name="Download" size={14} className="group-hover/btn:-translate-y-0.5 transition-transform duration-300" />
                    Export
                  </button>

                  <button
                    onClick={ontoggle}
                    className="h-9 px-2 text-gray-500 hover:text-pink-600 hover:bg-pink-50 rounded-lg transition-all duration-300 transform hover:scale-105"
                  >
                    <Icon name="ChevronUp" size={16} className="hover:translate-y-0.5 transition-transform duration-300" />
                  </button>
                </div>
              </div>

              {/* Active Tags with animation */}
              {hasActiveFilters && (
                <div className="flex flex-wrap gap-1.5 pt-2 border-t border-gray-100">
                  {searchTerm && (
                    <FilterTag label={searchTerm} onRemove={() => onSearchChange("")} color="blue" />
                  )}
                  {departmentFilter && (
                    <FilterTag label={departmentFilter} onRemove={() => onDepartmentChange("")} color="purple" />
                  )}
                  {employmentTypeFilter && (
                    <FilterTag label={employmentTypeFilter} onRemove={() => onEmploymentTypeChange("")} color="pink" />
                  )}
                  {startDate && (
                    <FilterTag label={`From ${startDate}`} onRemove={() => onStartDateChange("")} color="blue" />
                  )}
                  {endDate && (
                    <FilterTag label={`To ${endDate}`} onRemove={() => onEndDateChange("")} color="purple" />
                  )}
                </div>
              )}
            </div>

            {/* Animated pulse dots */}
            <div className="absolute top-3 right-3 flex space-x-1">
              <div className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-pulse"></div>
              <div className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-pulse" style={{ animationDelay: '75ms' }}></div>
              <div className="w-1.5 h-1.5 bg-pink-400 rounded-full animate-pulse" style={{ animationDelay: '150ms' }}></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const FilterTag = ({ label, onRemove, color = "blue" }) => {
  const colorClasses = {
    blue: "bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200",
    purple: "bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-200",
    pink: "bg-pink-50 hover:bg-pink-100 text-pink-700 border-pink-200"
  };

  return (
    <span 
      className={`group/tag inline-flex items-center gap-1.5 ${colorClasses[color]} border text-xs px-2.5 py-1.5 rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-sm animate-slideIn`}
    >
      <span className="max-w-[150px] truncate font-medium">{label}</span>
      <button 
        onClick={onRemove}
        className="text-current opacity-60 hover:opacity-100 transition-all duration-200 transform hover:rotate-90"
      >
        <Icon name="X" size={12} />
      </button>
    </span>
  );
};

export default EmployeeFilters;